import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateAttendanceReport, AttendanceSummary } from '../../api/attendanceApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one or more Roster files and one or more Punch files (both required).',
  'Optionally set an output filename.',
  'Hit Generate Report — the detailed + pivot attendance workbook downloads automatically.',
];

interface AttendancePanelProps {
  mission: Mission;
  onBack: () => void;
}

interface FileMultiDropProps {
  label: string;
  note: string;
  accept: string;
  hint: string;
  accentColor: string;
  files: File[];
  onFiles: (files: File[]) => void;
}

function FileMultiDrop({ label, note, accept, hint, accentColor, files, onFiles }: FileMultiDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    onFiles([...files, ...Array.from(incoming)]);
  };

  const removeFile = (index: number) => {
    onFiles(files.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-4">
      <Label className="mb-1 text-sm font-semibold text-foreground">
        {label} <span className="text-red-600">*</span>
      </Label>
      <p className="mb-2 text-xs text-muted-foreground">{note}</p>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors ${
          dragActive ? 'border-current bg-muted' : 'border-input bg-muted'
        }`}
        style={dragActive ? { borderColor: accentColor } : undefined}
      >
        <Button
          onClick={() => inputRef.current?.click()}
          style={{ backgroundColor: accentColor }}
          className="text-white hover:opacity-90"
        >
          <ArrowUpIcon /> Upload file(s)
        </Button>
        <span className="text-xs text-muted-foreground">or drag and drop files here</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700"
            >
              <span>📄 {file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="text-green-700 hover:text-green-900"
                aria-label={`Remove ${file.name}`}
              >
                <XIcon className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttendancePanel({ mission, onBack }: AttendancePanelProps) {
  const [rosterFiles, setRosterFiles] = useState<File[]>([]);
  const [punchFiles, setPunchFiles] = useState<File[]>([]);
  const [outputFilename, setOutputFilename] = useState('Attendance_Report.xlsx');
  const { status, error, result, fail, run } = useFileRun<FileResult<AttendanceSummary>>();

  const handleSubmit = async () => {
    if (rosterFiles.length === 0 || punchFiles.length === 0) {
      fail('Upload at least one Roster file and one Punch file before generating.');
      return;
    }
    await run(() => generateAttendanceReport(
      { rosterFiles, punchFiles },
      { outputFilename }
    ), 'Attendance report generation failed.');
  };

  const accept = '.xlsx,.xls';

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Expected inputs</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <strong>Roster</strong> files (monthly roster with a date-per-column layout) and{' '}
            <strong>Punch</strong> files (daily in/out punch exports) are both required — upload as
            many of each as you need.
          </li>
          <li>The output workbook has a Detailed sheet (per-punch) and a Pivot sheet (employee × date).</li>
          <li>All files must be Excel workbooks (.xlsx / .xls).</li>
        </ul>
      </div>

      <FileMultiDrop
        label="Roster Files"
        note="Monthly attendance roster(s), one row per employee, one column per date."
        accept={accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={rosterFiles}
        onFiles={setRosterFiles}
      />

      <FileMultiDrop
        label="Punch Files"
        note="Daily punch-clock export(s) with in-time / out-time columns."
        accept={accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={punchFiles}
        onFiles={setPunchFiles}
      />

      <div className="mb-6 mt-1 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="Attendance_Report.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Generating…' : 'Generate Report'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Generating…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          <p className="mb-1">✅ {result.filename} downloaded.</p>
          {result.summary && (
            <p className="text-xs text-green-800">
              {result.summary.employees} employees · {result.summary.dates} dates ·{' '}
              {result.summary.detailed_rows} punch rows
            </p>
          )}
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload roster and punch files above to get started.
        </div>
      )}
    </section>
  );
}

export default AttendancePanel;
